// 퀘스트 창의 내용 모델(순수 로직, DOM 없음). 예전 마을 카드가 한 번에 보이던 것(다음 목표·북쪽 문 둘·워프·일일 의뢰)을
// 목록 줄 + 상세 줄로 나눠 담고, 지역 기록(완료 탭)을 더한다. 문구·판정은 기존 모델(regionModel·villageQuestModel)의 것을 그대로 쓴다.
import { bountiesFor, codexCount, codexOf, codexSteps, codexTitle, huntTable, type AdventureView, type HuntDrop, type HuntRow } from '../shared/adventure';
import { NAMED_UNIQUES } from '../shared/content/items';
import { REGION_IDS, regionNoOf, type ExitDef, type RegionId } from '../shared/levels';
import { REGIONS } from '../shared/regions';
import type { RegionRecordView } from '../shared/types';
import { doorStageLine, recordText, regionCardText, starsText, villageDoorGuide, type ProgressLike, type TierTone } from './regionModel';
import { milestoneAction, milestoneClears, milestoneSub, nextMilestone } from './questMilestoneModel';
import { buildTrialRows, nextTrialRow, type TrialStateLike } from './trialModel';
import type { TrialBoardId } from '../shared/content/trials';
import { warpChips, type QuestAction, type QuestGoalView, type WarpChip } from './villageQuestModel';

export type QuestTab = 'active' | 'done' | 'adventure' | 'trial';
export type RowKind = 'goal' | 'milestone' | 'door' | 'warp' | 'daily' | 'record' | 'bounty' | 'hunt' | 'codex' | 'trial' | 'board';
/** 줄의 색: 다음 목표·입문·도전·잠김·완료 */
export type RowTone = 'gold' | TierTone | 'lock' | 'done';

/** 상세 칸의 큰 버튼이 하는 일. 창을 닫은 뒤 실행한다(창이 열린 동안은 자동 이동·가방이 막힌다) */
export type QuestRun =
  | { readonly kind: 'auto'; readonly action: QuestAction }
  | { readonly kind: 'daily' }
  /** 도감 단계 보상 받기(창을 닫지 않고 바로 보낸다) */
  | { readonly kind: 'claim'; readonly step: number }
  /** 용사의 시련 도전(창을 닫은 뒤 enterTrial) */
  | { readonly kind: 'trial'; readonly id: string }
  /** 시련 상자 열기(창을 닫지 않고 바로 보낸다) */
  | { readonly kind: 'trialChest'; readonly board: TrialBoardId };

export interface QuestRowAction {
  readonly label: string;
  readonly run: QuestRun;
}

export interface QuestRow {
  readonly id: string;
  readonly kind: RowKind;
  readonly title: string;
  /** 제목 옆 작은 표지(다음 목표·입문·도전·잠김·완주 …) */
  readonly tag: string;
  readonly tone: RowTone;
  /** 목록 둘째 줄 */
  readonly sub: string;
  /** 상세 설명 줄들 */
  readonly lines: readonly string[];
  /** 보상 줄들 */
  readonly rewards: readonly string[];
  /** 큰 버튼. 없으면 null */
  readonly action: QuestRowAction | null;
  /** 막힌 이유(큰 버튼 대신 보인다). 없으면 '' */
  readonly blocked: string;
  /** 워프 줄이면 마을 칩들 */
  readonly chips?: readonly WarpChip[];
  /** 지역 줄이면 그 지역(엠블럼) */
  readonly region?: RegionId;
}

export interface QuestWindowInput {
  readonly goal: QuestGoalView | null;
  /** 지금 마을 이름(헤더) */
  readonly place: string;
  readonly town: number;
  readonly highestTown: number;
  readonly townNames: readonly string[];
  readonly exits: readonly ExitDef[];
  /** 지역 열림 판정(null이면 모두 열림) */
  readonly open: ((id: string) => boolean) | null;
  readonly progress?: ProgressLike;
  readonly hard?: ProgressLike & { readonly on: boolean };
  readonly records: Readonly<Record<RegionId, RegionRecordView>> | undefined;
  /** 영웅 레벨(이정표 줄). 없으면 이정표 줄을 만들지 않는다 */
  readonly level?: number;
  /** 깬 일반·하드 스테이지 수(이관 계정은 스테이지별 횟수가 비어 이 수로 판정한다) */
  readonly clearedNormal?: number;
  readonly clearedHard?: number;
  /** 용사의 시련 상태(view.trials). 없으면 「다음 시련」 줄을 만들지 않는다 */
  readonly trials?: TrialStateLike;
}

export interface QuestWindowModel {
  readonly active: readonly QuestRow[];
  readonly done: readonly QuestRow[];
  /** 모험 탭(현상금·유니크 사냥·도감). buildQuestWindow는 비워 두고 창이 buildAdventureRows로 채운다 */
  readonly adventure: readonly QuestRow[];
  /** 시련 탭(시련판 3개 × (머리 줄 + 시련 10개)) */
  readonly trial: readonly QuestRow[];
}

/** 목표 「자동」 칸이 하는 일의 버튼 글자 */
export function goalActionLabel(a: QuestAction): string {
  return a.kind === 'bag' ? '가방 열기' : a.kind === 'door' ? '문까지 자동 이동' : a.kind === 'gate' ? '윤회의 문까지 자동 이동' : '자동 이동';
}

/** 한 지역의 스테이지 클리어 수(n-1 ~ n-10 중 한 번이라도 깬 것) */
export function clearedStages(progress: ProgressLike | undefined, region: RegionId): number {
  if (!progress) return 0;
  const no = regionNoOf(region);
  let n = 0;
  for (let i = 1; i <= 10; i++) if (progress.stageClears(`r${no}-s${String(i).padStart(2, '0')}`) > 0) n++;
  return n;
}

export function buildQuestWindow(i: QuestWindowInput): QuestWindowModel {
  const active: QuestRow[] = [];
  const g = i.goal;
  if (g) {
    const a = g.action;
    active.push({
      id: 'goal', kind: 'goal', title: g.name, tag: '다음 목표', tone: 'gold',
      sub: g.change || g.detail,
      lines: g.detail ? [g.detail] : [],
      rewards: g.change ? ['달성하면 ' + g.change] : [],
      action: a ? { label: goalActionLabel(a), run: { kind: 'auto', action: a } } : null,
      blocked: a ? '' : '지금은 자동으로 갈 수 있는 곳이 없어요',
      region: a?.kind === 'door' ? a.region : undefined,
    });
  }
  // 이정표(3개월 C3): 스테이지 첫 클리어·레벨·하드 보스를 '다음 이정표' 한 줄로. 새 보상은 없고, 지역 문이 열려 있으면 그 문으로 자동 이동할 수 있다
  if (i.level !== undefined) {
    const next = nextMilestone({ level: i.level, clears: milestoneClears(i.progress && { stageClears: (id) => i.progress!.stageClears(id), cleared: i.clearedNormal }, i.hard && { stageClears: (id) => i.hard!.stageClears(id), cleared: i.clearedHard }) });
    if (next) {
      const a = milestoneAction(next.def), locked = !!a && a.kind === 'door' && i.open !== null && !i.open(a.region);
      active.push({
        id: 'milestone', kind: 'milestone', title: next.def.name, tag: '이정표', tone: 'gold',
        sub: milestoneSub(next), lines: [next.def.hint, '이정표 ' + next.remaining + '개 더 남음'], rewards: [],
        action: a && !locked ? { label: goalActionLabel(a), run: { kind: 'auto', action: a } } : null, blocked: locked ? '아직 열리지 않은 지역이에요' : '',
        region: a?.kind === 'door' && !locked ? a.region : undefined,
      });
    }
  }
  // 다음 시련(3개월 C7): 이정표 바로 아래. 새 이용자가 시련을 새 목표로 알아보게 한다
  const nt = i.trials ? nextTrialRow(i.trials) : null;
  if (nt) active.push(nt);
  // 던전 문(화면 왼쪽→오른쪽, 위치 말은 villageDoorGuide 방위어): 문 앞 카드(regionCard)와 같은 문구
  for (const d of villageDoorGuide(i.exits)) {
    const locked = i.open !== null && !i.open(d.id);
    const info = REGIONS[d.id];
    const line = doorStageLine(i.progress, i.hard, d.id);
    const t = regionCardText(info, i.records?.[d.id]);
    active.push({
      id: 'door:' + d.id, kind: 'door', title: (d.side ? d.side + ' · ' : '') + d.name, tag: locked ? '잠김' : d.tier, tone: locked ? 'lock' : d.tone,
      sub: locked ? line.detail : (line.label ? line.label + ' · ' : '') + line.detail,
      lines: [t.blurb, '적 · ' + t.enemies, '위험 · ' + t.hazards.join(' · '), '보스 · ' + t.boss, '추천 · ' + t.recommend],
      rewards: [t.reward],
      action: locked ? null : { label: '문까지 자동 이동', run: { kind: 'auto', action: { kind: 'door', region: d.id } } },
      blocked: locked ? line.detail : '',
      region: d.id,
    });
  }
  const chips = warpChips(i.town, i.highestTown, i.townNames);
  if (chips.length) {
    active.push({
      id: 'warp', kind: 'warp', title: '마을 워프', tag: '이동', tone: 'gold',
      sub: '도착했던 마을 ' + chips.length + '곳', lines: ['도착했던 마을로 바로 이동해요. 지금 있는 마을은 누를 수 없어요.'], rewards: [],
      action: null, blocked: '', chips,
    });
  }
  active.push({
    id: 'daily', kind: 'daily', title: '일일 의뢰', tag: '매일', tone: 'gold',
    sub: '오늘의 의뢰 · 일일 던전 · 무한의 탑',
    lines: ['매일 새로 열리는 의뢰와 던전이에요. 의뢰 보상은 게시판에서 받아요.'], rewards: [],
    action: { label: '일일 의뢰 열기', run: { kind: 'daily' } }, blocked: '',
  });

  const done: QuestRow[] = [];
  for (const id of REGION_IDS) {
    const rec = i.records?.[id];
    const n = clearedStages(i.progress, id);
    if (!rec || (rec.clears <= 0 && n === 0)) continue;
    const info = REGIONS[id];
    const all = n >= 10;
    done.push({
      id: 'rec:' + id, kind: 'record', title: info.name, tag: all ? '완주' : n + '/10', tone: all ? 'done' : 'gold',
      sub: '스테이지 ' + n + '/10 · ' + starsText(rec.bestStars),
      lines: [recordText(rec), all ? '모든 스테이지를 깼어요' : '남은 스테이지 ' + (10 - n) + '개'], rewards: [],
      action: null, blocked: '', region: id,
    });
  }
  return { active, done, adventure: [], trial: i.trials ? buildTrialRows(i.trials) : [] };
}

/** 단축키·가방 줄 버튼이 퀘스트 창을 열 수 있는 상황인가: 마을 장면에서 일시정지·다른 창·이동 연출이 없을 때만(전투 중인 던전에서는 열지 않는다) */
export function canOpenQuestWindow(s: { scene: 'village' | 'other'; paused: boolean; anyWindow: boolean; traveling: boolean }): boolean {
  return s.scene === 'village' && !s.paused && !s.anyWindow && !s.traveling;
}

// ── 모험 탭(모험 목표판, PRD 2절) ──
/** 확률 표시: 0.03 → 3%, 0.005 → 0.5% */
export function chanceText(c: number): string { return String(Math.round(c * 10000) / 100) + '%'; }
const BOSS_LABEL = { mid: '중간보스', final: '지역보스' } as const;

function dropLine(d: HuntDrop, got: boolean): string {
  return (d.kind === 'unique' ? '유니크 · ' : '아바타 · ') + d.name + ' ' + chanceText(d.chance) + (got ? ' 획득' : '');
}
function huntAction(r: HuntRow): { action: QuestRowAction | null; blocked: string } {
  if (!r.playable) return { action: null, blocked: '아직 갈 수 없는 지역이에요 (준비 중)' };
  return { action: { label: '그 지역으로', run: { kind: 'auto', action: { kind: 'door', region: ('r' + r.region) as RegionId } } }, blocked: '' };
}

/** 모험 목표판 줄: 오늘의 현상금(최대 3) → 유니크 사냥 표(지역 1~6 × 중간·지역보스) → 도감. 보상은 득템 판정 확률뿐(도감 보상은 아직 없음) */
export function buildAdventureRows(adv: AdventureView | undefined, ownedAvatars: readonly string[]): QuestRow[] {
  if (!adv) return [];
  const hunt = huntTable();
  const rows: QuestRow[] = [];
  const foundSet = new Set(adv.found), ownedSet = new Set(ownedAvatars);
  for (const b of bountiesFor(adv.day, adv.highestTown)) {
    const row = hunt.find((h) => h.stageId === b.stageId);
    const done = adv.done.includes(b.id);
    const { action, blocked } = row ? huntAction(row) : { action: null, blocked: '아직 갈 수 없는 지역이에요' };
    rows.push({
      id: 'bounty:' + b.id, kind: 'bounty', title: b.boss, tag: done ? '완료' : '현상금', tone: done ? 'done' : 'gold',
      sub: (row?.regionName ?? '') + ' · ' + BOSS_LABEL[b.tier],
      lines: ['잡으면 이 보스의 득템 판정 1회 추가 (오늘 1번)', done ? '오늘 현상금은 이미 받았어요' : '아직 안 잡았어요', 'KST 0시에 바뀜'],
      rewards: row ? row.drops.map((d) => d.name + ' ' + chanceText(d.chance)) : [],
      action, blocked,
    });
  }
  for (const h of hunt) {
    rows.push({
      id: 'hunt:' + h.stageId, kind: 'hunt', title: h.boss, tag: h.playable ? '유니크 사냥' : '준비 중', tone: h.playable ? 'gold' : 'lock',
      sub: h.regionName + ' · ' + BOSS_LABEL[h.tier],
      lines: ['처치할 때마다 득템 판정 (확률은 보스 한 번당)'],
      rewards: h.drops.map((d) => dropLine(d, d.kind === 'unique' ? foundSet.has(d.id) : ownedSet.has(d.id))),
      ...huntAction(h),
    });
  }
  const cx = codexOf(adv.found, ownedAvatars);
  const uniq = NAMED_UNIQUES.map((u) => (foundSet.has(u.id) ? '획득 · ' : '미획득 · ') + u.name);
  const count = codexCount(cx), claimed = adv.claimed ?? [];
  const steps = codexSteps(count, claimed);
  const next = steps.find((x) => x.can);
  const title = codexTitle(claimed);
  const stepLines = steps.map((x) => (x.claimed ? '받음 · ' : x.reached ? '받기 가능 · ' : '앞으로 ' + x.need + '종 · ') + x.reward.step + '종 ' + x.reward.name);
  rows.push({
    id: 'codex', kind: 'codex', title: '도감', tag: next ? '받을 보상' : title ?? '수집', tone: 'gold',
    sub: '모은 ' + count + '종 (유니크 ' + cx.uniques.found + '/' + cx.uniques.total + ' · 아바타 ' + cx.avatars.found + '/' + cx.avatars.total + ')' + (title ? ' · 「' + title + '」' : ''),
    lines: ['한 번이라도 얻은 이름 있는 유니크와 지금 가진 아바타예요. 보상은 전투 능력치가 아니에요.', ...stepLines, ...uniq],
    rewards: steps.map((x) => x.reward.step + '종: ' + x.reward.desc),
    action: next ? { label: next.reward.step + '종 보상 받기', run: { kind: 'claim', step: next.reward.step } } : null,
    blocked: next ? '' : steps.every((x) => x.claimed) ? '도감 보상을 모두 받았어요' : '다음 보상까지 ' + (steps.find((x) => !x.reached)?.need ?? 0) + '종 남았어요',
  });
  return rows;
}

/** 도감 보상을 받은 직후 같은 자리 버튼이 다음 단계(5종 → 10종)로 바뀌어도 연타가 그 단계까지 받지 않게 하는 잠금 시간(ms) */
export const CLAIM_LOCK_MS = 600;

/** 보상 받기 클릭을 받은 시각 now 기준의 잠금 끝 시각 */
export function claimLockUntil(now: number): number { return now + CLAIM_LOCK_MS; }

/** 잠금 중(now < lockUntil)이면 보상 받기 클릭을 무시한다 */
export function claimBlocked(now: number, lockUntil: number): boolean { return now < lockUntil; }
